import { Fragment } from "react";

import { EMPTY_MARK, formatQty } from "@buildpy/core/lib/format";
import Money from "@/components/money";
import {
  mergedAmountOf,
  mergedDeltaOf,
  statementAppendixOf,
  statementPrintModeOf,
  statementQtyText,
  statementSummaryRows,
  statementTagOf,
  type CurrentStatement,
  type StatementAudience,
  type StatementDelta,
} from "@buildpy/core/modules/change-order/current-statement";
import PrintSheet, { PrintUnit, SheetField, SheetTitle, TD, TH } from "@/modules/print/sheet";

interface Props {
  statement: CurrentStatement;
  contractNo: string;
  contractTitle: string;
  // 기준 시각(인쇄한 날).
  asOf: string;
  audience?: StatementAudience;
}

// 금액 칸 — 폭과 상관없이(폰·종이 포함) 숫자 중간에서 꺾지 않는다.
const NUM = "whitespace-nowrap break-normal text-right tabular-nums";
// 차수 열이 4~5개면 글자·여백을 줄여 A4 폭에 넣는다.
const DENSE = "px-1 text-[10px]";

const signedQty = (qty: number): string => `${qty > 0 ? "+" : qty < 0 ? "-" : ""}${formatQty(Math.abs(qty))}`;

// 증감 칸 — 금액 아래 수량(줄인 열·단위가 섞인 합계는 수량을 뺀다).
const DeltaBody = ({ d, withQty }: { d: StatementDelta | null; withQty: boolean }) =>
  d ? (
    <>
      <Money signed value={d.amount} />
      {withQty && <div className="text-[10px] text-[#737373]">{signedQty(d.qty)}</div>}
    </>
  ) : (
    <>{EMPTY_MARK}</>
  );

// 현재 내역서 인쇄본 — 화면 표와 같은 CurrentStatement·합계 줄 한 벌을 흰 종이에 찍는다.
const CurrentStatementDocument = ({ statement, contractNo, contractTitle, asOf, audience = "staff" }: Props) => {
  const mode = statementPrintModeOf(statement.columns.length);
  const merged = mode === "merged";
  const col = mode === "dense" ? `${NUM} ${DENSE}` : NUM;
  const width = 4 + (merged ? 1 : statement.columns.length);
  return (
    <PrintSheet>
      <SheetTitle>현재 내역서</SheetTitle>
      <div className="mb-4 border-t border-[#e5e5e5]">
        <SheetField label="계약">
          {contractNo} · {contractTitle}
        </SheetField>
        <SheetField label="기준">
          {asOf} · 승인된 변경공사 {statement.columns.length}건 반영
        </SheetField>
        {statement.columns.map((c) => (
          <SheetField key={c.id} label={`${c.seq}차 변경`}>
            {c.title} · <Money signed value={c.delta_total} />
          </SheetField>
        ))}
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr>
            <TH className="text-left">항목</TH>
            <TH>단위</TH>
            <TH className="text-right">원 계약</TH>
            {merged ? (
              <TH className="text-right">변경 합계</TH>
            ) : (
              statement.columns.map((c) => (
                <TH key={c.id} className={mode === "dense" ? `text-right ${DENSE}` : "text-right"}>
                  {c.seq}차
                </TH>
              ))
            )}
            <TH className="text-right">현재</TH>
          </tr>
        </thead>
        <tbody>
          {statement.groups.map((group) => (
            <Fragment key={group.key}>
              <tr>
                <TD className="bg-[#f5f5f5] font-semibold" colSpan={width}>
                  {group.label}
                </TD>
              </tr>
              {group.lines.map((line) => {
                const tag = statementTagOf(line, statement.grouping, group.kind);
                return (
                  <tr key={line.key}>
                    <TD>
                      {line.label}
                      {tag && <div className="text-[10px] text-[#737373]">{tag}</div>}
                    </TD>
                    <TD className="text-center"><PrintUnit unit={line.unit} /></TD>
                    <TD className={NUM}>
                      <Money value={line.original_amount} />
                      <div className="text-[10px] text-[#737373]">{formatQty(line.original_qty)}</div>
                    </TD>
                    {merged ? (
                      <TD className={col}>
                        <DeltaBody d={mergedDeltaOf(line.deltas)} withQty={!line.unit_mixed} />
                      </TD>
                    ) : (
                      line.deltas.map((d, i) => (
                        <TD key={statement.columns[i].id} className={col}>
                          <DeltaBody d={d} withQty={mode === "each"} />
                        </TD>
                      ))
                    )}
                    <TD className={`${NUM} font-medium`}>
                      <Money value={line.current_amount} />
                      {statementQtyText(line, audience) !== null && (
                        <div className="text-[10px] font-normal text-[#737373]">{statementQtyText(line, audience)}</div>
                      )}
                    </TD>
                  </tr>
                );
              })}
              <tr>
                <TD className="bg-[#fafafa]" colSpan={2}>
                  {group.label} 소계
                </TD>
                <TD className={`${NUM} bg-[#fafafa]`}><Money value={group.original} /></TD>
                {(merged ? [mergedAmountOf(group.deltas) ?? 0] : group.deltas).map((d, i) => (
                  <TD key={merged ? "merged" : statement.columns[i].id} className={`${col} bg-[#fafafa]`}>
                    {d === 0 ? EMPTY_MARK : <Money signed value={d} />}
                  </TD>
                ))}
                <TD className={`${NUM} bg-[#fafafa] font-semibold`}><Money value={group.current} /></TD>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>

      <table className="mt-4 w-full border-collapse">
        <tbody>
          {statementSummaryRows(statement, audience).map((row) => {
            const strong = row.key === "total";
            const bg = strong ? "bg-[#f5f5f5] font-bold" : "";
            return (
              <tr key={row.key}>
                <TD className={bg}>
                  {row.label}
                  {row.note && <span className="ml-1 text-[10px] font-normal text-[#737373]">({row.note})</span>}
                </TD>
                <TD className={`${bg} ${NUM}`}>{row.original === null ? EMPTY_MARK : <Money value={row.original} />}</TD>
                {(merged ? [mergedAmountOf(row.deltas)] : row.deltas).map((d, i) => (
                  <TD key={merged ? "merged" : statement.columns[i].id} className={`${bg} ${col}`}>
                    {d === null ? EMPTY_MARK : <Money signed value={d} />}
                  </TD>
                ))}
                <TD className={`${bg} ${NUM}`}><Money value={row.current} /></TD>
              </tr>
            );
          })}
        </tbody>
      </table>

      {merged && (
        <table className="mt-4 w-full border-collapse" data-statement-appendix="">
          <thead>
            <tr>
              <TH className="text-left">차수별 변경 · 항목</TH>
              <TH>단위</TH>
              <TH className="text-right">수량</TH>
              <TH className="text-right">증감액</TH>
            </tr>
          </thead>
          <tbody>
            {statementAppendixOf(statement).map(({ column, lines }) => (
              <Fragment key={column.id}>
                <tr>
                  <TD className="bg-[#f5f5f5] font-semibold" colSpan={3}>
                    {column.seq}차 · {column.title}
                  </TD>
                  <TD className={`${NUM} bg-[#f5f5f5] font-semibold`}><Money signed value={column.delta_total} /></TD>
                </tr>
                {lines.map((l) => (
                  <tr key={l.key}>
                    <TD>{l.label}</TD>
                    <TD className="text-center"><PrintUnit unit={l.unit} /></TD>
                    <TD className={NUM}>{signedQty(l.qty)}</TD>
                    <TD className={NUM}><Money signed value={l.amount} /></TD>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      <p className="mt-2 text-xs text-[#737373]">
        현재 계약금액 <Money value={statement.totals.total.current} /> (공급가 <Money value={statement.totals.current_supply} /> · 부가세{" "}
        <Money value={statement.totals.current_vat} />) · 승인된 변경공사만 반영합니다.
      </p>
    </PrintSheet>
  );
};

export default CurrentStatementDocument;
